/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService } from '../core';

/**
 * Public Link Generation Request
 */
export interface GenerateLinkRequest {
  team_id?: string;
  expiresInSeconds?: number; // Seconds until expiration
  password?: string;
  maxUses?: number;
  description?: string;
}

/**
 * Public Link Response
 */
export interface PublicLink {
  linkId: string;
  itemId: string;
  itemType: 'content' | 'view';
  itemName?: string;
  token: string;
  fullUrl: string;
  createdAt: string;
  expiresAt?: string;
  isActive: boolean;
  accessCount: number;
  hasPassword: boolean;
  maxUses?: number;
  createdBy: string;
  teamId: string;
}

/**
 * My Links Query Parameters
 */
export interface MyLinksParams {
  teamId?: string;
  includeRevoked?: boolean;
  includeExpired?: boolean;
  page?: number;
  limit?: number;
}

/**
 * My Links Response
 */
export interface MyLinksResponse {
  links: PublicLink[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
  };
}

/**
 * Public Links Service
 *
 * Handles all public link operations including:
 * - Generating shareable links for content and views
 * - Listing user's created links
 * - Revoking and deleting links
 * - Accessing public links
 */
export class PublicLinksService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  /**
   * Generate a public link for content
   */
  async generateContentLink(contentId: string, request: GenerateLinkRequest): Promise<PublicLink> {
    try {
      console.log(`Generating public link for content: ${contentId}`, request);

      const response = await this.post<PublicLink>(
        `/content/${contentId}/links`,
        request
      );

      // Invalidate my-links cache
      this.invalidateCache('content');

      return response;
    } catch (error) {
      console.error(`Error generating link for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `PublicLinksService.generateContentLink(${contentId})`);
    }
  }

  /**
   * Generate a public link for a view
   */
  async generateViewLink(viewId: string, request: GenerateLinkRequest): Promise<PublicLink> {
    try {
      console.log(`Generating public link for view: ${viewId}`, request);

      const response = await this.post<PublicLink>(
        `/views/${viewId}/links`,
        request
      );

      // Invalidate my-links cache
      this.invalidateCache('content');

      return response;
    } catch (error) {
      console.error(`Error generating link for view ${viewId}:`, error);
      throw this.errorHandler.handleError(error, `PublicLinksService.generateViewLink(${viewId})`);
    }
  }

  /**
   * List all links created by current user
   */
  async getMyLinks(params?: MyLinksParams): Promise<MyLinksResponse> {
    try {
      console.log('Fetching my public links with params:', params);

      const response = await this.get<MyLinksResponse>('/links/my-links', {
        params,
        timeout: 10000
      });

      console.log(`Retrieved ${response.links?.length || 0} public links`);
      return response;
    } catch (error) {
      console.error('Error fetching my links:', error);
      throw this.errorHandler.handleError(error, 'PublicLinksService.getMyLinks');
    }
  }

  /**
   * List public links for specific content
   */
  async getContentLinks(contentId: string): Promise<PublicLink[]> {
    try {
      console.log(`Fetching public links for content: ${contentId}`);

      const response = await this.get<PublicLink[]>(
        `/content/${contentId}/links`
      );

      return response;
    } catch (error) {
      console.error(`Error fetching links for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `PublicLinksService.getContentLinks(${contentId})`);
    }
  }

  /**
   * List public links for specific view
   */
  async getViewLinks(viewId: string): Promise<PublicLink[]> {
    try {
      console.log(`Fetching public links for view: ${viewId}`);

      const response = await this.get<PublicLink[]>(
        `/views/${viewId}/links`
      );

      return response;
    } catch (error) {
      console.error(`Error fetching links for view ${viewId}:`, error);
      throw this.errorHandler.handleError(error, `PublicLinksService.getViewLinks(${viewId})`);
    }
  }

  /**
   * Revoke a public link (soft delete - sets isActive to false)
   */
  async revokeLink(linkId: string, teamId?: string): Promise<{ message: string }> {
    try {
      console.log(`Revoking public link: ${linkId}`);

      const response = await this.patch<{ message: string }>(
        `/links/${linkId}/revoke`,
        {},
        {
          params: teamId ? { teamId } : undefined
        }
      );

      // Invalidate caches
      this.invalidateCache('content');

      return response;
    } catch (error) {
      console.error(`Error revoking link ${linkId}:`, error);
      throw this.errorHandler.handleError(error, `PublicLinksService.revokeLink(${linkId})`);
    }
  }

  /**
   * Delete a public link permanently (hard delete)
   */
  async deleteLink(linkId: string, teamId?: string): Promise<{ message: string }> {
    try {
      console.log(`Deleting public link: ${linkId}`);

      const response = await this.delete<{ message: string }>(
        `/links/${linkId}`,
        {
          params: teamId ? { teamId } : undefined
        }
      );

      // Invalidate caches
      this.invalidateCache('content');

      return response;
    } catch (error) {
      console.error(`Error deleting link ${linkId}:`, error);
      throw this.errorHandler.handleError(error, `PublicLinksService.deleteLink(${linkId})`);
    }
  }

  /**
   * Access a public link (no authentication required)
   * Uses token and optional password as query parameters
   */
  async accessPublicLink(
    linkId: string,
    token: string,
    password?: string,
    shareToken?: string
  ): Promise<any> {
    try {
      console.log(`Accessing public link: ${linkId}`);

      const params: any = { token };
      if (password) {
        params.password = password;
      }
      if (shareToken) {
        params.shareToken = shareToken;
      }

      const response = await this.get<any>(
        `/public/${linkId}`,
        { params }
      );

      return response;
    } catch (error) {
      console.error(`Error accessing public link ${linkId}:`, error);
      throw this.errorHandler.handleError(error, `PublicLinksService.accessPublicLink(${linkId})`);
    }
  }

  /**
   * Get download URL for a public link (no authentication required)
   */
  async getPublicLinkDownloadUrl(linkId: string, token: string, password?: string): Promise<{ downloadUrl: string }> {
    try {
      console.log(`Getting download URL for public link: ${linkId}`);

      const params: any = { token };
      if (password) {
        params.password = password;
      }

      const response = await this.get<{ downloadUrl: string }>(
        `/public/${linkId}/download`,
        { params }
      );

      return response;
    } catch (error) {
      console.error(`Error getting download URL for link ${linkId}:`, error);
      throw this.errorHandler.handleError(error, `PublicLinksService.getPublicLinkDownloadUrl(${linkId})`);
    }
  }

  /**
   * Resolve a single embedded component (ContentPick / ViewContentQuery) for an
   * anonymous visitor. Returns tiles each with a signed access_token for click-through.
   */
  async resolveComponent(
    linkId: string,
    componentId: string,
    params: {
      // Top-level auth: user-facing share token + optional password OR
      // share-session token (proves a prior password gate clearance).
      token?: string;
      password?: string;
      shareToken?: string;
      // Nested auth: a content access token previously minted for a view-target tile.
      accessToken?: string;
      page?: number;
      pageSize?: number;
    }
  ): Promise<{
    items: Array<{
      id: string;
      title: string;
      kind: 'content' | 'view';
      mimeType?: string;
      thumbnailUrl?: string;
      description?: string;
      category?: string;
      accessToken: string;
      fileSize?: string;
      fileSizeBytes?: number;
      fileName?: string;
      createdAt?: string;
      status?: string;
      viewCount?: number;
      downloadCount?: number;
      averageRating?: number;
      tagIds?: string[];
      authorName?: string;
    }>;
    pagination: { currentPage: number; pageSize: number; totalCount: number; hasNextPage: boolean } | null;
  }> {
    try {
      const response = await this.post<any>(
        `/public/${linkId}/components/${componentId}/resolve`,
        {
          token: params.token,
          password: params.password,
          shareToken: params.shareToken,
          accessToken: params.accessToken,
          page: params.page,
          pageSize: params.pageSize,
        }
      );
      return response;
    } catch (error) {
      throw this.errorHandler.handleError(
        error,
        `PublicLinksService.resolveComponent(${linkId}, ${componentId})`
      );
    }
  }

  /**
   * Fetch a content item via a signed access token (anonymous).
   */
  async getPublicContent(accessToken: string): Promise<any> {
    try {
      const response = await this.get<any>('/public/content', { params: { access: accessToken } });
      return response;
    } catch (error) {
      throw this.errorHandler.handleError(error, 'PublicLinksService.getPublicContent');
    }
  }

  /**
   * Fetch a download URL for a content item via a signed access token (anonymous).
   */
  async getPublicContentDownload(accessToken: string): Promise<{ downloadUrl: string; urlExpiresIn: number; fileName: string }> {
    try {
      const response = await this.get<{ downloadUrl: string; urlExpiresIn: number; fileName: string }>(
        '/public/content/download',
        { params: { access: accessToken } }
      );
      return response;
    } catch (error) {
      throw this.errorHandler.handleError(error, 'PublicLinksService.getPublicContentDownload');
    }
  }

}

// Export singleton instance
export const publicLinksService = new PublicLinksService();
